import React from 'react';
import { Link } from 'react-router-dom';
import KmoCta from '../KmoCta';
import BmbCta from '../BmbCta';
import MkPhone from '../MkPhone';
import MkPill from '../MkPill';
import MkReviewCard from '../MkReviewCard';
import MkStars from '../MkStars';
import MkWave from '../MkWave';
import kmoQuickLock from '../../../assets/images/mk/mk-kmo-quicklock-510x740.webp';
import bmbBreathing from '../../../assets/images/mk/mk-bmb-breathing-540.webp';
import bestappBadge from '../../../assets/images/mk/mk-bestapp-badge-240.webp';
import updatestarBadge from '../../../assets/images/mk/mk-updatestar-4stars-118.webp';
import { BESTAPP_URL, UPDATESTAR_URL } from '../../../marketing/links';
import { MK_FLAGS } from '../../../marketing/flags';
import { PLAY_FACTS } from '../../../marketing/facts';
import { REVIEWS } from '../../../marketing/reviews';
import { mkTrack } from '../../../marketing/track';

// H1 — the only <h1> on /. Hero text is static (no reveal, no opacity animation) so it can be the LCP.
const HomeHero = ({ platform }) => {
  const ios = platform === 'ios';
  return (
    <section className="mk-hero section" id="top" aria-labelledby="mk-hero-title">
      <MkWave />
      <div className="container">
        <div className="mk-hero__inner section-inner">
          <div className="mk-hero__copy">
            {/* Two third-party awards, side by side — the first thing a cold visitor sees. */}
            <ul className="list-reset mk-hero__awards">
              <li>
                <a
                  className="mk-award-pill"
                  href={BESTAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => mkTrack('outbound_click', 'bestapp:home_hero')}
                >
                  <img src={bestappBadge} alt="" width={32} height={35} />
                  <span className="mk-award-pill__text">
                    <strong>“Best for Device Locking”</strong>
                    <span>BestApp.com</span>
                  </span>
                  <span className="screen-reader"> (opens in a new tab)</span>
                </a>
              </li>
              <li>
                <a
                  className="mk-award-pill"
                  href={UPDATESTAR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => mkTrack('outbound_click', 'updatestar:home_hero')}
                >
                  <img src={updatestarBadge} alt="" width={44} height={36} />
                  <span className="mk-award-pill__text">
                    <strong>“Very Good” award</strong>
                    <span>UpdateStar user rating</span>
                  </span>
                  <span className="screen-reader"> (opens in a new tab)</span>
                </a>
              </li>
            </ul>
            <h1 id="mk-hero-title" className="mk-h1">
              Lock your phone.
              <br />
              <span className="mk-grad">Get your time back.</span>
            </h1>
            <p className="mk-lead mk-hero__lead">
              Keep Me Out locks your Android phone for as long as you choose — for study, sleep or focused work. No
              willpower required.
            </p>
            <div id="mk-hero-cta" className="mk-hero__cta">
              {ios ? (
                <>
                  <p className="mk-platform-note">
                    On an iPhone? Keep Me Out is Android-only. Its companion app, Bring Me Back, works on iPhone:
                  </p>
                  <BmbCta variant="badges" placement="home_hero_ios" platform={platform} />
                  <Link
                    className="mk-textlink"
                    to="/bring-me-back"
                    onClick={() => mkTrack('cta_click', 'bmb_page:home_hero_ios')}
                  >
                    What Bring Me Back does →
                  </Link>
                </>
              ) : (
                <KmoCta placement="home_hero" platform={platform} />
              )}
            </div>
            <ul className="list-reset mk-proofrow">
              {MK_FLAGS.SHOW_PLAY_RATING ? (
                <li className="mk-proofrow__rating">
                  <strong aria-hidden="true">
                    {PLAY_FACTS.rating}
                    <span className="mk-proofrow__star">★</span>
                  </strong>
                  <MkStars
                    value={Number(PLAY_FACTS.rating)}
                    label={`Rated ${PLAY_FACTS.rating} out of 5 on Google Play`}
                  />
                  <span>Google Play rating</span>
                </li>
              ) : (
                <li>
                  <strong>{PLAY_FACTS.since}</strong>
                  <span>on Google Play since</span>
                </li>
              )}
              <li>
                <strong>{PLAY_FACTS.downloads}</strong>
                <span>downloads</span>
              </li>
              <li>
                <strong>{PLAY_FACTS.ratings}</strong>
                <span>ratings</span>
              </li>
            </ul>
            <p className="mk-micro mk-hero__caption">{`Keep Me Out on Google Play, ${PLAY_FACTS.asOf}.`}</p>
            {!ios && (
              <Link
                className="mk-companion"
                to="/bring-me-back"
                onClick={() => mkTrack('cta_click', 'bmb_page:home_hero_companion')}
              >
                <MkPill variant="new">New</MkPill>
                <span>Bring Me Back: a gentle pause before distracting apps, for iPhone and Android →</span>
              </Link>
            )}
          </div>

          <div className="mk-hero__visual">
            <div className="mk-drift">
              <div className="mk-hero__stage">
                <div className="mk-hero__phone mk-hero__phone--kmo">
                  <div className="mk-float">
                    <MkPhone
                      src={kmoQuickLock}
                      width={510}
                      height={740}
                      fade
                      eager
                      priority
                      alt="Keep Me Out Quick Lock screen with hour and minute dials and a Lock button"
                    />
                  </div>
                </div>
                <div className="mk-hero__phone mk-hero__phone--bmb">
                  <div className="mk-float mk-float--late">
                    <MkPhone
                      src={bmbBreathing}
                      width={540}
                      height={1095}
                      framed
                      eager
                      alt={'Bring Me Back breathing pause before Facebook, reading "Breathe out"'}
                    />
                  </div>
                </div>
                <span className="mk-hero__tag mk-hero__tag--kmo" aria-hidden="true">
                  Keep Me Out · the lock
                </span>
                <span className="mk-hero__tag mk-hero__tag--bmb" aria-hidden="true">
                  Bring Me Back · the pause
                </span>
                <MkReviewCard review={REVIEWS.lucy} variant="compact" className="mk-hero__review" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeHero;
